import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { ContactDetailsContainer } from '@components/ContactDetails/CommonStyles/ContactDetailsContainer';

import { addModelLog } from '@libs/NwLogs';

import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { useContact } from '@hooks/API/Contact/UseContact';

import ModelDetailsBooksWrapper from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksWrapper';

import ContactDetailsActions from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsActions';
import ModelDetailsHeaderDrawer from '@components/ContactDetails/ContactDetailsHeader/ModelDetailsHeaderDrawer';
import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import FeaturesDrawer from '@components/Features/FeaturesDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ModelAgenciesCard from '@components/ModelAgencies/ModelAgenciesCard';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import ModelDrawerAgenda from '@components/Schedule/Agenda/ModelDrawerAgenda';
import ContactSocials from '@components/Social/ContactSocials';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Actions = styled(FlexContainer)`
    gap: 2rem;
    margin-right: 1rem;
`;

const ModelDrawerAgendaOnly = ({ modelID, calendarDefaultDate, onClose }) => {
    const { data: currentContact, isLoading, error } = useContact(modelID);
    const [{ areaContext }] = useStateValue();
    const navigate = useNavigate();
    const [isOpenHistory, setIsOpenHistory] = useState(false);
    const [editFeatures, setEditFeatures] = useState(false);

    const viewModelAgencies = useUserPermission(
        'ViewModelAgencies',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewModelFeatures = useUserPermission(
        'ViewModelFeatures',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    useEffect(() => {
        currentContact &&
            currentContact.NetworkAgencies &&
            !currentContact.NetworkAgencies[0].StorageBooking &&
            addModelLog(currentContact, areaContext.area);
    }, [currentContact]);

    const handleMoveModelDetails = () => {
        navigate(`/model/${modelID}`);
    };

    const handleCloseFeatureDrawer = () => {
        setEditFeatures(false);
    };

    const handleOpenFeatureDrawer = () => {
        setEditFeatures(true);
    };

    const handleOpenHistory = () => {
        setIsOpenHistory(true);
    };

    const handleCloseHistory = () => {
        setIsOpenHistory(false);
    };

    const renderAction = (content, permission) => {
        return <PermissionBlock content={content} permission={permission} />;
    };

    return (
        <NwDrawer
            width={780}
            title={
                currentContact ? (
                    <FlexContainer justifyContent="space-between">
                        <div>Model Preview</div>
                        <Actions>
                            <NwIcon
                                icon={byPrefixAndName.fass['expand-wide']}
                                pointer
                                onClick={handleMoveModelDetails}
                                size="lg"
                            />
                            {renderAction(
                                <NwIcon
                                    icon={byPrefixAndName.fasl['weight']}
                                    size="lg"
                                    onClick={viewModelFeatures && handleOpenFeatureDrawer}
                                />,
                                viewModelFeatures
                            )}
                            <ObjectHistory iconOnly onIconClick={handleOpenHistory} object={currentContact} />
                            <ContactDetailsActions contact={currentContact} fromDrawer onClose={onClose} />
                        </Actions>
                    </FlexContainer>
                ) : (
                    'Model Preview'
                )
            }
            onClose={onClose}
            whiteBg={false}
        >
            <div className="drawer-content">
                {isLoading || !currentContact ? (
                    <Loading />
                ) : (
                    <ContactDetailsContainer>
                        <ModelDetailsHeaderDrawer
                            selectedModel={currentContact.ID}
                            onOpenModelAgencies={() => {}}
                            onOpenStatements={() => {}}
                        />
                        <div className="content-section">
                            <ModelDrawerAgenda
                                defaultDate={calendarDefaultDate}
                                type="model"
                                contactId={currentContact.ID}
                            />
                        </div>
                        <div className="content-section">
                            <ContactSocials previewMode currentContact={currentContact} />
                        </div>
                        <div className="content-section">
                            <ModelDetailsBooksWrapper
                                contactID={modelID}
                                scope="model-drawer"
                                modelPlaylists={currentContact.Playlists}
                            />
                        </div>
                        {viewModelAgencies && (
                            <div className="content-section">
                                <ModelAgenciesCard
                                    contactID={currentContact.ID}
                                    represented={currentContact.Represented}
                                    drawerMode
                                />
                            </div>
                        )}
                    </ContactDetailsContainer>
                )}
                {editFeatures && (
                    <FeaturesDrawer contact={currentContact} readOnly={true} onClose={handleCloseFeatureDrawer} />
                )}
            </div>
        </NwDrawer>
    );
};

export default ModelDrawerAgendaOnly;
